import { useParams } from '@reach/router';
import { graphql } from 'gatsby';
import React, { FC, useState } from 'react';

import { useGetPlayerRgRiskAssessmentOverviewPageLink } from '@/bits/links/useLink';
import { Card, CardBody, CardOptions, CardOptionsButton } from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import { booleanFilter } from '@/components/DataGrid/values';
import { NextIcon, PreviousIcon, RefreshIcon } from '@/components/icons';
import { useTranslate } from '@/contexts';
import { assert } from '@/utils/error';
import usePlayerRgAutomationHistoryLegacy from './usePlayerRgAutomationHistoryLegacy';

export const Fragment = graphql`
  fragment SanityPlayerRgAutomationHistoryLegacyBlock on SanityPlayerRgAutomationHistoryLegacyBlock {
    title {
      ...SanityLocaleString
    }
  }
`;

const formatEvent = (event: string) => {
  return event
    .split(/(?=[A-Z])/)
    .join(' ')
    .toString();
};

const PlayerRgAutomationHistoryLegacyBlock: FC<{
  block: Queries.SanityPlayerRgAutomationHistoryLegacyBlockFragment;
}> = ({ block }) => {
  const params = useParams();
  const { t } = useTranslate();

  assert(params.playerId, 'missing playerId');

  // Legacy data is rarely needed, so the card starts collapsed and the query
  // stays paused until someone expands it.
  const [collapsed, setCollapsed] = useState(true);

  const {
    history,
    fetching,
    nextPage,
    previousPage,
    refresh,
    defaultFilter,
    updateFilter,
  } = usePlayerRgAutomationHistoryLegacy({ paused: collapsed });

  const getRgRiskAssessmentOverviewPage =
    useGetPlayerRgRiskAssessmentOverviewPageLink();

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'playerRgAutomationHistoryLegacy',
    data: history,
    fetching,
    setOrderBy: () => {},
    desc: true,
    defaultFilter,
    updateFilter,
    getRowLink: ({ row }) =>
      (row?.playerGlobalId &&
        getRgRiskAssessmentOverviewPage(row.playerGlobalId)) ??
      '',
    externalFilters: [
      booleanFilter({
        field: '__showSuspended',
        filterField: 'legacyShowSuspended',
        title: 'Show Suspended',
      }),
    ],
    schema: (s) => [
      s.dateTimeValue({
        field: 'processedAt',
        title: 'Processed At',
        fromFilterField: 'legacyFrom',
        toFilterField: 'legacyTo',
      }),
      s.stringValue({
        field: 'eventId',
        title: 'Event Id',
        getValue: ({ row }) => row.eventId,
      }),
      s.stringValue({
        field: 'eventType',
        title: 'Event Type',
        getValue: ({ row }) => formatEvent(row.eventType),
      }),
      s.stringValue({
        field: 'extraInformation',
        title: 'Extra Information',
        getValue: ({ row }) => row.extraInformation ?? '',
      }),
      s.stringValue({
        field: 'regionId',
        title: 'Region Id',
        getValue: ({ row }) => row.regionId,
        hidden: true,
      }),
      s.stringValue({
        field: 'regionName',
        title: 'Region Name',
        getValue: ({ row }) => row.regionName,
      }),
      s.stringValue({
        field: 'riskId',
        title: 'Risk Id',
        getValue: ({ row }) => row.riskId,
        hidden: true,
      }),
      s.stringValue({
        field: 'riskName',
        title: 'Risk Name',
        getValue: ({ row }) => row.riskName,
      }),
      s.stringValue({
        field: 'riskLevelBefore',
        title: 'Risk Level Before',
        getValue: ({ row }) => row.riskLevelBefore,
      }),
      s.stringValue({
        field: 'riskLevelAfter',
        title: 'Risk Level After',
        getValue: ({ row }) => row.riskLevelAfter,
      }),
      s.stringValue({
        field: 'suspendedBy',
        title: 'Suspended By',
        hidden: true,
        getValue: ({ row }) => row.suspendedBy,
      }),
    ],
  });

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      collapsible
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />
          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default PlayerRgAutomationHistoryLegacyBlock;
